/**
 * Pull a French error message out of an axios error response, falling back to
 * `fallback` if the shape is unexpected.
 */
export function extractApiMessage(err: unknown, fallback: string): string {
  if (typeof err !== 'object' || err === null) return fallback
  const e = err as {
    response?: { data?: { error?: { message?: string }; message?: string } }
  }
  return e.response?.data?.error?.message ?? e.response?.data?.message ?? fallback
}

/** Returns the backend error code (e.g. "SUBSCRIBER_NOT_FOUND") if present. */
export function extractApiCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null
  const e = err as { response?: { data?: { error?: { code?: string } } } }
  return e.response?.data?.error?.code ?? null
}

/** Returns the HTTP status from an axios-shaped error, or null. */
export function extractApiStatus(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null
  const e = err as { response?: { status?: number } }
  return e.response?.status ?? null
}

/**
 * Returns the backend `error.details` object if present — e.g. the
 * `{ nextAllowedAt }` payload of a ZONE_CHANGE_COOLDOWN (429) error.
 */
export function extractApiDetails(err: unknown): Record<string, unknown> | null {
  if (typeof err !== 'object' || err === null) return null
  const e = err as { response?: { data?: { error?: { details?: Record<string, unknown> } } } }
  return e.response?.data?.error?.details ?? null
}

/**
 * Parses the `Retry-After` header (delay in seconds or an HTTP date) from an
 * axios-shaped error into milliseconds, or `null` when absent/unparseable.
 */
export function extractRetryAfterMs(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null
  const e = err as { response?: { headers?: Record<string, string> } }
  const raw = e.response?.headers?.['retry-after']
  if (!raw) return null
  const seconds = Number(raw)
  if (Number.isFinite(seconds)) return seconds * 1000
  const at = Date.parse(raw)
  return Number.isNaN(at) ? null : Math.max(0, at - Date.now())
}
